import {IconLock, IconX} from "@tabler/icons-react";
import {memo, useCallback, useEffect, useState} from "react";
import {createPortal} from "react-dom";
import monogramLight from "@/assets/images/brand/rrc-icon.svg";
import monogramDark from "@/assets/images/brand/rrc-icon.svg";
import {useAuthStore} from "@/store/useAuth.store";
import styles from "./StoryGate.module.css";

/**
 * Locked story that appears after GUEST_MAX_STORIES for unauthenticated users.
 * Shows a teaser ring in the carousel and a fullscreen CTA overlay on click.
 */
const StoryGate = () => {
  const [showOverlay, setShowOverlay] = useState(false);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const handleAuthForm = useAuthStore(state => state.handleAuthForm);

  const handleOpen = useCallback(() => setShowOverlay(true), []);
  const handleClose = useCallback(() => setShowOverlay(false), []);

  const handleRegister = useCallback(() => {
    setShowOverlay(false);
    handleAuthForm("registration");
    handleShowAuthModal();
  }, [handleAuthForm, handleShowAuthModal]);

  const handleLogin = useCallback(() => {
    setShowOverlay(false);
    handleAuthForm("login");
    handleShowAuthModal();
  }, [handleAuthForm, handleShowAuthModal]);

  useEffect(() => {
    if (!showOverlay) return;
    const onKey = e => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showOverlay, handleClose]);

  return (
    <>
      {/* Circle in carousel */}
      <div
        className={styles["story-gate"]}
        onClick={handleOpen}
        onKeyDown={e => (e.key === "Enter" || e.key === " ") && handleOpen()}
        role="button"
        tabIndex={0}
        aria-label="Desbloquear más historias"
      >
        <div className={styles["story-gate__ring"]}>
          <div className={styles["story-gate__avatar"]}>
            <span className={styles["story-gate__lock-icon"]}>
              <IconLock size={22} />
            </span>
          </div>
        </div>
        <span className={styles["story-gate__label"]}>Descubre más</span>
      </div>

      {/* Fullscreen overlay — portal to escape stacking context */}
      {showOverlay &&
        createPortal(
          <div
            className={styles["story-gate__overlay"]}
            role="dialog"
            aria-modal="true"
            aria-label="Desbloquea promociones exclusivas"
          >
            <div className={styles["story-gate__card"]}>
              <button
                type="button"
                className={styles["story-gate__card-close"]}
                onClick={handleClose}
                aria-label="Cerrar"
              >
                <IconX size={20} />
              </button>

              <div className={styles["story-gate__card-icon"]}>
                <img
                  src={monogramDark}
                  alt="Randrush"
                  className={styles["story-gate__monogram--dark"]}
                  width={40}
                  height={40}
                />
                <img
                  src={monogramLight}
                  alt="Randrush"
                  className={styles["story-gate__monogram--light"]}
                  width={40}
                  height={40}
                />
              </div>

              <h2 className={styles["story-gate__card-title"]}>¡Hay más por descubrir!</h2>

              <p className={styles["story-gate__card-desc"]}>
                Inicia sesión o regístrate para ver todas las historias. Al registrarte, recibe un giro gratis por cada
                40 Bs en tu primer depósito.
              </p>

              <div className={styles["story-gate__card-actions"]}>
                <button
                  type="button"
                  className={styles["story-gate__btn-register"]}
                  onClick={handleRegister}
                >
                  Registrarse
                </button>
                <button
                  type="button"
                  className={styles["story-gate__btn-login"]}
                  onClick={handleLogin}
                >
                  Iniciar sesión
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default memo(StoryGate);
